/**
 * Registro / entrar — ver `MEMBERSHIP_ROUTE.REGISTRO` y `MEMBERSHIP_ROUTE.ENTRAR`
 *
 * Post-pago: cuenta → `MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO` → pendiente → mi acceso.
 * Rutas canónicas y portal: `membershipAccessFlow.ts`.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { useSEO } from "@/hooks/useSEO";
import { useBrandPersonName } from "@/hooks/useBrandPersonName";
import { getRegistroClerkAppearance } from "@/lib/clerkRegistroAppearance";
import { DATAFAST_GOALS, trackDataFastGoal } from "@/lib/datafastAnalytics";
import { SignIn, SignUp, useAuth } from "@clerk/clerk-react";
import { Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { BRAND_SITE_URL } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";

interface RegistroProps {
  /** Si "sign-in", muestra ingreso en lugar de signup */
  mode?: "sign-up" | "sign-in";
}

export default function Registro({ mode = "sign-up" }: RegistroProps) {
  const brandPersonName = useBrandPersonName();
  useSEO(
    useMemo(
      () => ({
        title: "Completá tu registro",
        description: `Registrate para acceder a tu membresía en ${brandPersonName}.`,
        keywords: "",
        ogImage: `${BRAND_SITE_URL}/og.png`,
        canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.REGISTRO}`,
        robots: "noindex, nofollow" as const,
      }),
      [brandPersonName],
    ),
  );
  const { search, pathname } = useLocation();
  const isPortalFlow = pathname.startsWith("/portal/");
  const completarRegistroPath = isPortalFlow
    ? MEMBERSHIP_ROUTE.PORTAL_COMPLETAR_REGISTRO
    : MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO;
  const [params] = useSearchParams();
  const { isLoaded, isSignedIn } = useAuth();

  const clerkAppearance = useMemo(() => getRegistroClerkAppearance(), []);

  // DataFast funnel: registro started (post-pay or access request)
  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    const planTier = (params.get("plan_tier") || "").trim().toLowerCase() || undefined;
    const hasSession = Boolean(params.get("session_id") || params.get("subscription_id"));
    trackDataFastGoal(
      mode === "sign-up" ? DATAFAST_GOALS.registroStarted : "sign_in_started",
      {
        mode,
        ...(planTier ? { plan_tier: planTier } : {}),
        has_session: hasSession ? "true" : "false",
        from_mp: params.get("from_mp") === "true" ? "true" : "false",
      },
    );
  }, [isLoaded, isSignedIn, mode, params]);

  // DataFast: completed signup (Clerk session present after sign-up)
  useEffect(() => {
    if (!isLoaded || !isSignedIn || mode !== "sign-up") return;
    const planTier = (params.get("plan_tier") || "").trim().toLowerCase() || undefined;
    trackDataFastGoal(DATAFAST_GOALS.signup, {
      mode: "sign-up",
      ...(planTier ? { plan_tier: planTier } : {}),
      has_session: params.get("session_id") || params.get("subscription_id") ? "true" : "false",
    });
  }, [isLoaded, isSignedIn, mode, params]);

  // Preservar query params críticos para que /completar-registro los reciba
  const sessionId = params.get("session_id");
  const subscriptionId = params.get("subscription_id");
  const planTier = (params.get("plan_tier") || "").trim().toLowerCase();
  const fromVip =
    params.get("from_special_offer") === "1" ||
    params.get("from_vip") === "1" ||
    planTier === "vip" ||
    planTier === "platinum" ||
    planTier === "devil";
  const fromBasic = params.get("from_basic") === "1" || planTier === "basic";
  const fromStandard =
    params.get("from_standard") === "1" || (planTier === "standard" && !fromVip && !fromBasic);
  const fromMp = params.get("from_mp") === "true";
  /** Solicitud de acceso SIN pago (dual template VIP) → no afirmar "pago recibido". */
  const isAccessRequest = params.get("access_request") === "1";
  /** Recovery video "Ya pagué" → pending funnel (not Stripe). */
  const rawRedirect =
    params.get("redirect_url")?.trim() || params.get("next")?.trim() || "";
  const claimedPaid =
    params.get("claimed_paid") === "1" ||
    params.get("from_recovery") === "1" ||
    rawRedirect.includes("claimed_paid=1") ||
    rawRedirect.includes("from_recovery=1") ||
    rawRedirect.includes(MEMBERSHIP_ROUTE.PENDING_APPROVAL);

  const queryParts: string[] = [];
  if (fromVip) {
    queryParts.push("from_special_offer=1");
    queryParts.push("from_vip=1");
  }
  if (fromBasic) queryParts.push("from_basic=1");
  if (fromStandard) queryParts.push("from_standard=1");
  if (planTier) queryParts.push(`plan_tier=${encodeURIComponent(planTier)}`);
  if (fromMp) queryParts.push("from_mp=true");
  if (isAccessRequest) queryParts.push("access_request=1");
  if (claimedPaid) {
    queryParts.push("claimed_paid=1");
    queryParts.push("from_recovery=1");
  }
  if (sessionId) queryParts.push(`session_id=${encodeURIComponent(sessionId)}`);
  if (subscriptionId) queryParts.push(`subscription_id=${encodeURIComponent(subscriptionId)}`);
  const freeTelegramUrl = params.get("free_telegram_url");
  if (freeTelegramUrl) queryParts.push(`free_telegram_url=${encodeURIComponent(freeTelegramUrl)}`);
  // Prefill desde DualCTA recovery / free join
  const prefillEmail = (params.get("email") || "").trim().toLowerCase();
  if (prefillEmail.includes("@")) {
    queryParts.push(`email=${encodeURIComponent(prefillEmail)}`);
  }
  const fromDualCta = params.get("from_dual_cta") === "1";
  if (fromDualCta) queryParts.push("from_dual_cta=1");
  const query = queryParts.length ? `?${queryParts.join("&")}` : "";

  /** Tras signup siempre perfil → pending (con flags claimed_paid si aplica). */
  const forceRedirectUrl = `${completarRegistroPath}${query}`;

  /** Tras sign-in: ?next / ?redirect_url seguros, o completar con mismos params. */
  const safeExternalRedirect =
    rawRedirect.startsWith("/") && !rawRedirect.startsWith("//") ? rawRedirect : null;
  const redirectAfterSignIn =
    claimedPaid
      ? forceRedirectUrl
      : safeExternalRedirect || forceRedirectUrl;

  if (!isLoaded) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (isSignedIn) {
    // Ya logueado: claimed paid → completar (si hace falta) o pending via complete page
    return <Navigate to={redirectAfterSignIn} replace />;
  }

  const isSignUp = mode === "sign-up";

  /**
   * Contexto del paso, sólo cuando aporta algo que el widget de Clerk no dice.
   * Clerk ya rotula "Crea tu cuenta · para continuar en {brand}": repetirlo
   * arriba empujaba el formulario fuera de la primera pantalla en mobile.
   */
  const isFreeAccess = planTier === "free" || fromDualCta;
  const contextNote = isFreeAccess ? (
    <>
      <strong className="text-emerald-200">Acceso gratis</strong>
      {prefillEmail.includes("@") ? (
        <>
          {" · "}
          <span className="font-mono text-[11px] text-white/85">{prefillEmail}</span>
        </>
      ) : null}
      . Creá tu cuenta (1 min) y completá el perfil: el equipo revisa y te envía el link cuando
      corresponda. Sin tarjeta.
    </>
  ) : isAccessRequest ? (
    <>
      <strong className="text-amber-200">Después de crear tu cuenta</strong> el equipo revisará tu
      solicitud de acceso VIP y, si corresponde, te asignará el link privado (puede tardar unas
      horas según el horario).
    </>
  ) : fromVip || fromBasic || fromStandard || fromMp ? (
    <>
      {fromMp ? "Tu pago con Mercado Pago fue recibido. " : "Tu pago fue recibido. "}
      <strong className="text-amber-200">Creá tu cuenta y completá el perfil</strong>
      {fromBasic || fromStandard
        ? " — el equipo validará tu cuenta y te enviará el link de ingreso cuando corresponda."
        : " — es el paso obligatorio para acceder. Después el equipo te asigna el link privado (puede tardar unas horas según el horario)."}
    </>
  ) : null;

  return (
    <div
      className="min-h-[100dvh] bg-zinc-950 text-zinc-100 flex items-start justify-center px-4 sm:items-center"
      style={{
        paddingTop: "max(1rem, env(safe-area-inset-top, 1rem))",
        paddingBottom: "max(2rem, env(safe-area-inset-bottom, 2rem))",
      }}
    >
      <div className="mx-auto w-full max-w-md space-y-3">
        {contextNote && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-100">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden />
            <p>{contextNote}</p>
          </div>
        )}

        {/* Sin Card envolvente: el widget de Clerk ya trae su propia tarjeta y
            anidarlas duplicaba borde, padding y encabezado. */}
        {isSignUp ? (
          <SignUp
            appearance={clerkAppearance}
            routing="hash"
            forceRedirectUrl={forceRedirectUrl}
            fallbackRedirectUrl={forceRedirectUrl}
            signInUrl={search ? `${MEMBERSHIP_ROUTE.ENTRAR}${search}` : MEMBERSHIP_ROUTE.ENTRAR}
            signInForceRedirectUrl={redirectAfterSignIn}
            signInFallbackRedirectUrl={redirectAfterSignIn}
            {...(prefillEmail.includes("@")
              ? { initialValues: { emailAddress: prefillEmail } }
              : {})}
          />
        ) : (
          <SignIn
            appearance={clerkAppearance}
            routing="hash"
            forceRedirectUrl={redirectAfterSignIn}
            fallbackRedirectUrl={redirectAfterSignIn}
            signUpUrl={search ? `${MEMBERSHIP_ROUTE.REGISTRO}${search}` : MEMBERSHIP_ROUTE.REGISTRO}
            signUpForceRedirectUrl={forceRedirectUrl}
            signUpFallbackRedirectUrl={forceRedirectUrl}
            {...(prefillEmail.includes("@")
              ? { initialValues: { emailAddress: prefillEmail } }
              : {})}
          />
        )}

        <div className="rounded-xl border border-white/10 bg-zinc-900/60 px-4 py-3">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
            <span>El ingreso al grupo/link privado requiere una aprobación manual del equipo.</span>
          </div>
          <MembershipPortalHint className="mt-3 border-t border-white/10 pt-3" />
        </div>
      </div>
    </div>
  );
}
